import { useState, useCallback, useRef } from 'react';
import {
  getDriveToken,
  peekDriveToken,
} from '../utils/driveTokenStore';
import { downloadPublicDriveFile } from '../utils/driveApi';
import { useDriveAuth } from '../contexts/DriveAuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
const GOOGLE_APP_ID = GOOGLE_CLIENT_ID.split('-')[0] || '';

export interface GoogleDrivePickerState {
  isLoading: boolean;
  error: string | null;
}

export interface UseGoogleDrivePickerReturn {
  state: GoogleDrivePickerState;
  /** 未ログイン時の 1 クリック目: combined consent → 成功後に picker を貫通表示 (issue #35 UX 改善)。 */
  signInAndPick: () => Promise<void>;
  /** ログイン済 (isReady) 前提で cache 済 token から picker を開く。 consent は await しない (issue #35 課題1)。 */
  openPicker: () => void;
  downloadFromUrl: (url: string) => Promise<void>;
  isConfigured: boolean;
  /** combined consent 済み (両 scope fresh) か。 subscribe 由来で全 hook と同期 (issue #35 課題2)。 */
  isReady: boolean;
}

export function extractFileId(url: string): string | null {
  const patterns = [
    /\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function useGoogleDrivePicker(
  onFileReady: (file: File) => void,
): UseGoogleDrivePickerReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pickerLoadedRef = useRef(false);

  const isConfigured = !!(GOOGLE_CLIENT_ID && GOOGLE_API_KEY);

  // issue #79 Phase β: ログイン状態は DriveAuthContext に集約 (4 hook 間自動同期)。
  const auth = useDriveAuth();
  const isReady = auth.hasFullAccess;

  const getToken = useCallback(async (): Promise<string> => {
    // ユーザー操作 (Picker を開く等) 同期直下の取得。 silent を skip し consent 直行で
    // popup blocker を回避する (issue #35 P0 #57)。 401 リトライ (downloadFile 内) は silent 維持。
    // issue #123: drive.readonly を廃し drive.file 単一に統一 (CASA 回避・一般公開)。
    return await getDriveToken('file', { interactive: true });
  }, []);

  const downloadFile = useCallback(async (fileId: string, fileName: string) => {
    const token = await getToken();
    if (!token) throw new Error('Googleアカウントへのログインが必要です');

    setIsLoading(true);
    setError(null);

    try {
      const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true`;
      let res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // issue #35 M-1 / #123: 401 はトークン失効。scope='file' を取り直して 1 回だけリトライする。
      if (res.status === 401) {
        const fresh = await auth.refreshFileToken();
        res = await fetch(url, { headers: { Authorization: `Bearer ${fresh}` } });
      }

      if (res.status === 403) {
        throw new Error('このファイルへのアクセス権限がありません');
      }
      if (res.status === 404) {
        throw new Error('ファイルが見つかりません。URLを確認してください');
      }
      if (!res.ok) {
        throw new Error(`ファイルの取得に失敗しました (${res.status})`);
      }

      const blob = await res.blob();
      const safeName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      const file = new File([blob], safeName, { type: 'application/pdf' });
      onFileReady(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'ファイルの取得に失敗しました';
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [onFileReady, getToken, auth]);

  const ensurePickerLoaded = useCallback((): Promise<void> => {
    if (pickerLoadedRef.current) return Promise.resolve();
    return new Promise((resolve, reject) => {
      if (!window.gapi) {
        reject(new Error('Google APIが読み込まれていません。ページを再読み込みしてください'));
        return;
      }
      window.gapi.load('picker', () => {
        pickerLoadedRef.current = true;
        resolve();
      });
    });
  }, []);

  // PickerBuilder 構築 + 表示。 views は呼び出し側で ensurePickerLoaded 後に生成する
  // (google.picker は picker API ロード後にのみ参照可のため)。 PICKED で downloadFile を貫通。
  // options.navHidden=true (既定) は単一 View 用に左ナビを隠す (setFileIds 経路が該当)。
  // 複数 View を切り替えさせたい入力 Picker (issue #178) は navHidden:false で開く。
  // options.supportTeamDrives=true で共有ドライブ横断を有効化する (issue #178)。
  const presentPicker = useCallback((
    views: google.picker.DocsView[],
    token: string,
    options?: { navHidden?: boolean; supportTeamDrives?: boolean },
  ) => {
    const { navHidden = true, supportTeamDrives = false } = options ?? {};
    const pickerWidth = Math.min(window.innerWidth - 20, 1051);
    const pickerHeight = Math.min(window.innerHeight - 80, 650);

    const builder = new google.picker.PickerBuilder()
      .setAppId(GOOGLE_APP_ID)
      .setOAuthToken(token)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setOrigin(window.location.origin)
      .setLocale('ja')
      .setSize(pickerWidth, pickerHeight)
      .setCallback((data: google.picker.CallbackData) => {
        if (data.action === google.picker.Action.PICKED) {
          const doc = data.docs[0];
          // downloadFile は失敗時に自身で setError 済み。 fire-and-forget のため
          // reject は握りつぶす (unhandled rejection 回避)。
          downloadFile(doc.id, doc.name).catch(() => {});
        }
      });

    for (const view of views) {
      builder.addView(view);
    }
    if (navHidden) {
      builder.enableFeature(google.picker.Feature.NAV_HIDDEN);
    }
    if (supportTeamDrives) {
      builder.enableFeature(google.picker.Feature.SUPPORT_TEAM_DRIVES);
    }

    builder.build().setVisible(true);
  }, [downloadFile]);

  const showPicker = useCallback(async (token: string) => {
    await ensurePickerLoaded();

    // issue #178: マイドライブ + 共有アイテム(Shared with me) + 共有ドライブ(Team Drive) の 3 View。
    // OAuth スコープは drive.file のまま (Picker 選択で per-file 許可)。View 追加のみで拡張する。
    const pdfView = () =>
      new google.picker.DocsView()
        .setIncludeFolders(true)
        .setMimeTypes('application/pdf')
        .setMode(google.picker.DocsViewMode.LIST);

    const myDriveView = pdfView().setParent('root');
    const sharedView = pdfView().setOwnedByMe(false);
    const teamDriveView = pdfView().setEnableTeamDrives(true);

    // 3 View 間を切り替えられるよう NAV_HIDDEN を外し、共有ドライブ横断に SUPPORT_TEAM_DRIVES を付ける。
    presentPicker([myDriveView, sharedView, teamDriveView], token, {
      navHidden: false,
      supportTeamDrives: true,
    });
  }, [ensurePickerLoaded, presentPicker]);

  // 未ログイン時の 1 クリック目 (issue #35 UX 改善, spec §4.2 案 A): consent を実行し、
  // 成功したら同期で drive.file token を peek して picker を「貫通」表示する (Pending Intent = 1 動作)。
  // picker 表示は showPicker = iframe DOM オーバーレイ (window.open でない) のため transient
  // activation は不要で、 consent callback 直後の表示も Google Picker 公式準拠で安全 (spec §3/§4.2)。
  // 失敗/キャンセル時は catch して return し、 picker には到達しない (構造的保証、 spec §4.3)。
  // consent で drive.file が付与されなければ peek=null となり picker を開かない (spec §4.1)。
  const signInAndPick = useCallback(async () => {
    setError(null);
    try {
      await auth.signInInteractive();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Googleへのログインに失敗しました';
      setError(msg);
      return;
    }
    // consent 成功直後: cache hit した drive.file token を同期取得して picker を開く。
    const token = peekDriveToken('file');
    if (!token) return;
    try {
      await showPicker(token);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Google Driveを開けませんでした';
      setError(msg);
    }
  }, [showPicker, auth]);

  // ログイン済 (isReady) 前提で picker を開く (issue #35 課題1)。
  // cache 済 drive.file token を同期で読み、 consent を await せず setVisible する
  // (consent 往復のタイミングが課題1 の原因のため、 cache hit + 新 gesture 直下に揃える)。
  // token が無ければ何もしない (UI 側で signIn を促す)。
  const openPicker = useCallback(async () => {
    setError(null);
    const token = peekDriveToken('file');
    if (!token) return;
    try {
      await showPicker(token);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Google Driveを開けませんでした';
      setError(msg);
    }
  }, [showPicker]);

  // issue #128: URL 貼り付けの裏実装をハイブリッド化する (drive.readonly は使わない = CASA 対象外)。
  // (a) 公開共有ファイル (「リンクを知る全員が閲覧可」) は API キーのみで直DL する (OAuth 不要・0クリック)。
  // (b) 非公開/本人所有 (直DL が 403/404/CORS で失敗) は fileId を DocsView.setFileIds([id]) で事前選択した
  //     Picker を出し、 ユーザーの 1 タップ確認で per-file の drive.file access を得てから downloadFile する。
  //     setFileIds は setParent / setEnableDrives と併用不可のため単独指定する。
  const downloadFromUrl = useCallback(async (url: string) => {
    setError(null);
    const fileId = extractFileId(url);
    if (!fileId) {
      setError('Google DriveのURLを認識できませんでした');
      return;
    }

    // (a) 公開共有ファイル: API キー直DL を先に試す。 成功なら OAuth も Picker も不要 (0クリック)。
    setIsLoading(true);
    try {
      const blob = await downloadPublicDriveFile(fileId, GOOGLE_API_KEY);
      const file = new File([blob], 'drive.pdf', { type: 'application/pdf' });
      onFileReady(file);
      return;
    } catch {
      // 非公開/権限外 (403/404) or CORS/network 失敗 → (b) Picker フォールバックへ落とす。
    } finally {
      setIsLoading(false);
    }

    // (b) 非公開/本人所有: setFileIds Picker (drive.file 1タップ認可) にフォールバック。
    try {
      const token = await getToken();
      if (!token) throw new Error('Googleアカウントへのログインが必要です');

      await ensurePickerLoaded();

      // setFileIds は Google Picker API 仕様上「カンマ区切り文字列」を取る (配列不可)。
      // 配列を渡すと fileId が不正シリアライズされ Picker が空 (「ドキュメントはありません」) になる (issue #128 実機バグ)。
      // また setMimeTypes は付けない: URL 貼付で fileId は 1 件に確定済みで冗長な上、
      // 保存時の実 mimeType が application/pdf と厳密一致しないと該当ファイルが除外される事故を防ぐ。
      const view = new google.picker.DocsView()
        .setFileIds(fileId)
        .setMode(google.picker.DocsViewMode.LIST);

      presentPicker([view], token);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'ファイルの取得に失敗しました';
      setError(msg);
    }
  }, [onFileReady, getToken, ensurePickerLoaded, presentPicker]);

  return {
    state: { isLoading, error },
    signInAndPick,
    openPicker,
    downloadFromUrl,
    isConfigured,
    isReady,
  };
}
